import {
  Box,
  Button,
  CircularProgress,
  Stack,
  Step,
  StepButton,
  StepLabel,
  Stepper,
} from "@mui/material";
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from "react-router-dom";
import Icon from "../../common/components/fundamentals/Icon";
import { useUser } from "../../common/context/UserProvider";
import { borderStyle, boxHeight, smallGap } from "../../styles/consts";
import { hasSalesOrAdminAccess } from "../../utils";
import { useOffer } from "../context/OfferProvider";
import { useStepNavigation } from "../context/StepNavigationContext";
import { useEmailFeedback } from "../context/EmailFeedbackContext";

interface StepNavigationProps {
  candidateEmail?: string;
  onNext?: () => void;
  onPrevious?: () => void;
  nextDisabled?: boolean;
  nextLoading?: boolean;
  previousDisabled?: boolean;
  nextLabel?: string;
  previousLabel?: string;
  hasUnsavedChanges?: boolean;
  onBeforeStepChange?: () => Promise<boolean>; // Return false to cancel navigation
}

export default function StepNavigation({
  candidateEmail,
  onNext,
  onPrevious,
  nextDisabled = false,
  nextLoading = false,
  previousDisabled = false,
  nextLabel = "Next",
  previousLabel = "Previous",
  hasUnsavedChanges,
  onBeforeStepChange,
}: StepNavigationProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const { offerId } = useParams();
  const [searchParams] = useSearchParams();
  const { setCandidateStep } = useStepNavigation();
  const { user } = useUser();
  const { offer } = useOffer();
  const { hasChanges: contextHasChanges, setShowFeedbackPopup, setPendingNavigation } = useEmailFeedback();

  const hasChanges = hasUnsavedChanges !== undefined ? hasUnsavedChanges : contextHasChanges;

  const isOnEmailReviewStep = location.pathname.includes("email-review");

  const currentCandidate = candidateEmail || searchParams.get("currentCandidate");
  const displayedMatch = offer?.matches?.find(
    (match) => match.candidate?.email === currentCandidate
  );

  const isCandidate = displayedMatch?.candidate
    ? user.email === displayedMatch.candidate.email
    : false;
  const isSupervisor = displayedMatch?.candidate
    ? user.email === displayedMatch.candidate.supervisor_email
    : false;

  const hasSalesView = hasSalesOrAdminAccess(user) && !isCandidate;

  // Candidates skip Email Review; non-supervisor/non-sales see only Overall Review.
  const steps = isCandidate
    ? [
        { label: "Overall Review", path: "review" },
        { label: "CV Adaptation", path: "cv-adaptation" },
        { label: "Feedback", path: "feedback" },
      ]
    : (isSupervisor || hasSalesView)
    ? [
        { label: "Overall Review", path: "review" },
        { label: "CV Adaptation", path: "cv-adaptation" },
        { label: "Email Review", path: "email-review" },
        { label: "Feedback", path: "feedback" },
      ]
    : [
        { label: "Overall Review", path: "review" },
      ];

  const getCurrentStepFromPath = () => {
    const pathSegments = location.pathname.split("/");
    const currentPath = pathSegments[pathSegments.length - 1].split("?")[0];

    const stepIndex = steps.findIndex((step) => step.path === currentPath);
    return stepIndex !== -1 ? stepIndex : 0;
  };

  const activeStep = getCurrentStepFromPath();

  const doStepNavigation = (stepIndex: number) => {
    const step = steps[stepIndex];
    if (candidateEmail) {
      setCandidateStep(candidateEmail, stepIndex + 1);
    }
    const currentCandidateParam =
      candidateEmail || searchParams.get("currentCandidate");
    const queryString = currentCandidateParam
      ? `?currentCandidate=${encodeURIComponent(currentCandidateParam)}`
      : "";
    navigate(`/alqemi/${offerId}/${step.path}${queryString}`);
  };

  const handleStepClick = async (stepIndex: number) => {
    if (onBeforeStepChange) {
      const canProceed = await onBeforeStepChange();
      if (!canProceed) return;
    }

    if (isOnEmailReviewStep && hasChanges) {
      setPendingNavigation(() => () => doStepNavigation(stepIndex));
      setShowFeedbackPopup(true);
    } else {
      doStepNavigation(stepIndex);
    }
  };

  const handleNext = () => {
    if (onNext) {
      onNext();
    } else if (activeStep < steps.length - 1) {
      handleStepClick(activeStep + 1);
    }
  };

  const handlePrevious = () => {
    if (onPrevious) {
      onPrevious();
    } else if (activeStep > 0) {
      handleStepClick(activeStep - 1);
    }
  };

  return (
    <Box
      sx={{
        borderTop: borderStyle,
        paddingLeft: smallGap,
        paddingRight: smallGap,
        display: "flex",
        height: boxHeight,
        backgroundColor: "white",
        gap: 2,
        boxSizing: "border-box",
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ width: "100%" }}
      >
        <Button
          variant="outlined"
          onClick={handlePrevious}
          disabled={activeStep === 0 || previousDisabled}
          sx={{ minWidth: 120 }}
        >
          {previousLabel}
        </Button>

        <Stepper
          activeStep={activeStep}
          alternativeLabel
          nonLinear
          sx={{ flex: 1, mx: 3 }}
        >
          {steps.map((step, index) => (
            <Step key={step.label} completed={false}>
              <StepButton
                onClick={() => handleStepClick(index)}
                sx={{
                  cursor: "pointer",
                  padding: "4px",
                  margin: "-4px",
                  borderRadius: "0px",
                  "&:hover": {
                    backgroundColor: "rgba(0, 0, 0, 0.04)",
                  },
                }}
              >
                <StepLabel
                  sx={{
                    "& .MuiStepLabel-labelContainer": {
                      marginTop: "-0.875rem",
                      cursor: "pointer",
                    },
                    "& .MuiStepLabel-label": {
                      cursor: "pointer",
                    },
                  }}
                >
                  {step.label}
                </StepLabel>
              </StepButton>
            </Step>
          ))}
        </Stepper>

        <Button
          variant="outlined"
          onClick={handleNext}
          disabled={nextDisabled || nextLoading}
          sx={{
            minWidth: 120,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          {nextLabel === "Submit CV" && (
            <Icon name="Check" sx={{ width: 20, height: 20 }} />
          )}
          {nextLabel}
          {nextLabel === "Send out application" &&
            (nextLoading ? (
              <Box
                sx={{
                  width: 20,
                  height: 20,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <CircularProgress size={16} color="inherit" />
              </Box>
            ) : (
              <Icon name="SendRight" sx={{ width: 20, height: 20 }} />
            ))}
        </Button>
      </Stack>
    </Box>
  );
}
